/**
 * Sync Status Page
 * Shows all pending transactions and conflicts for review
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOfflineSync, useOnlineStatus } from '../hooks/useOfflineSync';
import { useSocket } from '../hooks/useSocket';
import { useBranch } from '../contexts/BranchContext';
import PendingTransactionsList from '../components/PendingTransactionsList';
import ConflictResolutionModal from '../components/ConflictResolutionModal';

export default function SyncStatus({ apiUrl }) {
  const isOnline = useOnlineStatus();
  const { effectiveBranchId } = useBranch();
  const {
    isInitialized,
    status,
    pendingTransactions,
    conflicts,
    retry,
    skip,
    sync,
    refreshStatus
  } = useOfflineSync(apiUrl + '/api', effectiveBranchId);

  const [selectedConflict, setSelectedConflict] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'conflicts' | 'history'
  const [wsKey, setWsKey] = useState(0);

  // WebSocket: refresh sync status when transactions arrive from server
  useSocket(apiUrl, {
    'transaction:created': () => setWsKey(k => k + 1),
    'transaction:updated': () => setWsKey(k => k + 1),
  }, { token: localStorage.getItem('adminToken') });

  // Trigger refreshStatus on WebSocket event
  useEffect(() => {
    if (!isInitialized) return;
    refreshStatus();
  }, [wsKey]);

  // Reduced fallback polling (30s) for local IndexedDB changes not triggered by WebSocket
  useEffect(() => {
    if (!isInitialized) return;
    const interval = setInterval(() => refreshStatus(), 30000);
    return () => clearInterval(interval);
  }, [isInitialized, refreshStatus]);

  const handleResolveConflict = async (offlineId, resolution, notes) => {
    if (resolution === 'skip') {
      await skip(offlineId, notes);
    } else if (resolution === 'retry') {
      await retry(offlineId);
    }
    setSelectedConflict(null);
    refreshStatus();
  };

  if (!isInitialized) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        กำลังโหลด...
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>📤 สถานะการซิงค์</h2>
        <p>ตรวจสอบและจัดการรายการที่รอซิงค์</p>
      </div>

      {/* Status Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Online Status */}
        <div style={{
          background: isOnline ? '#dcfce7' : '#fef3c7',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>
            {isOnline ? '🌐' : '📵'}
          </div>
          <div style={{
            fontWeight: '700',
            color: isOnline ? '#166534' : '#b45309'
          }}>
            {isOnline ? 'ออนไลน์' : 'ออฟไลน์'}
          </div>
        </div>

        {/* Pending */}
        <div style={{
          background: status.pending > 0 ? '#fef3c7' : '#f3f4f6',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>
            {status.isSyncing ? '🔄' : '⏳'}
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#b45309' }}>
            {status.pending}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {status.isSyncing ? 'กำลังซิงค์' : 'รอซิงค์'}
          </div>
        </div>

        {/* Conflicts */}
        <div style={{
          background: status.conflicts > 0 ? '#fee2e2' : '#f3f4f6',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: status.conflicts > 0 ? '#dc2626' : '#6b7280'
          }}>
            {status.conflicts}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>ต้องตรวจสอบ</div>
        </div>

        {/* Synced */}
        <div style={{
          background: '#dcfce7',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#166534' }}>
            {status.synced}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>ซิงค์แล้ว</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={sync}
          disabled={!isOnline || status.isSyncing || status.pending === 0}
          style={{
            padding: '12px 24px',
            background: (!isOnline || status.isSyncing || status.pending === 0)
              ? '#e5e7eb'
              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: (!isOnline || status.isSyncing || status.pending === 0) ? '#9ca3af' : 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: (!isOnline || status.isSyncing || status.pending === 0) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {status.isSyncing ? (
            <>
              <span style={{
                width: '16px',
                height: '16px',
                border: '2px solid white',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }} />
              กำลังซิงค์...
            </>
          ) : (
            <>🔄 ซิงค์ตอนนี้</>
          )}
        </button>

        <button
          onClick={refreshStatus}
          style={{
            padding: '12px 24px',
            background: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          🔃 รีเฟรช
        </button>

        <Link
          to="/checkout"
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          💰 กลับไปคิดเงิน
        </Link>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'pending' ? 'white' : 'transparent',
            color: activeTab === 'pending' ? '#2563eb' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'pending' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          รอซิงค์ ({status.pending})
        </button>
        <button
          onClick={() => setActiveTab('conflicts')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'conflicts' ? 'white' : 'transparent',
            color: activeTab === 'conflicts' ? '#dc2626' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'conflicts' ? '2px solid #dc2626' : '2px solid transparent',
            marginBottom: '-2px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ต้องตรวจสอบ ({status.conflicts})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'pending' && (
        <PendingTransactionsList
          transactions={pendingTransactions.filter(t =>
            t.syncStatus === 'pending' ||
            t.syncStatus === 'syncing' ||
            t.syncStatus === 'failed' ||
            t.syncStatus === 'needs_review'
          )}
          onRetry={retry}
          onSkip={skip}
        />
      )}

      {activeTab === 'conflicts' && (
        <div>
          {conflicts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <p style={{ margin: 0 }}>ไม่มีรายการที่ต้องตรวจสอบ</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {conflicts.map((conflict, idx) => (
                <div
                  key={idx}
                  onClick={() => setSelectedConflict(conflict)}
                  style={{
                    background: 'white',
                    color: '#2d3748',
                    border: '2px solid #fecaca',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <span style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        โต๊ะ {conflict.transaction?.tableCode}
                      </span>
                      <span style={{
                        marginLeft: '10px',
                        color: '#dc2626',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {conflict.conflictType === 'ALREADY_CHECKED_OUT' && 'คิดเงินไปแล้ว'}
                        {conflict.conflictType === 'SESSION_MISMATCH' && 'โต๊ะเปิดใหม่'}
                        {conflict.conflictType === 'TABLE_ALREADY_CLOSED' && 'โต๊ะปิดแล้ว'}
                      </span>
                    </div>
                    <div style={{ color: '#dc2626', fontSize: '18px' }}>
                      ⚠️
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conflict Resolution Modal */}
      {selectedConflict && (
        <ConflictResolutionModal
          conflict={selectedConflict}
          onResolve={handleResolveConflict}
          onClose={() => setSelectedConflict(null)}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
